import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import Select from 'react-select';
import ColumnsDevider from '../../../customControl/ColumnsDevider';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { TravelAgentAssignmentModel } from '../../../Models/TravelAgentModels';
import { loaderActions } from '../../../_actions/loader.actions';
import { getAllData, postUsersData, verifytoken } from '../../../services/ApiCallingUserServices';
import { ClearLocalStorage, GetLocalStorage } from '../../../components/commonClass/Session';
import { ApiNotRespondMessage } from '../../../components/commonClass/CommonValidation';
import { regionAssignment, TravelAgentAssignmentRef, travelAgentProps } from './TravelAgent.interface';

const TravelAgentAssignment = forwardRef<TravelAgentAssignmentRef, travelAgentProps>((props: travelAgentProps, ref) => {
  const { tabId, selectedTabId, isSaved, setIsSaved, isConfirm, setIsConfirm, setIsRedirect, TravelAgentId, travelAgentOptionList, locationsData, warningMessages, setWarningMessages, domainWiseDisable } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [formValue, setFormValue] = useState(new TravelAgentAssignmentModel());
  const [formError, setFormError] = useState<any>(null);

  const [disabled, setDisabled] = useState(location?.state?.disabled ?? true);
  const [editDisabled, setEditDisabled] = useState(location?.state?.editdisabled ?? true);
  const [clearDisabled, setClearDisabled] = useState(true);

  //#region API calling function
  const gettravelagentassignmentbyid = async (id: number) => {
    const travelagentassignmentbyid = await getAllData(`/midoffice/v1/gettravelagentassignmentbyid?travelAgentId=${id}`)
    if (travelagentassignmentbyid?.status && travelagentassignmentbyid?.data) {
      const retrieveData = travelagentassignmentbyid?.data?.[0];
      setFormValue((form) => ({
        ...form,
        SalesManagerUserId: retrieveData?.SalesManagerUserId ? retrieveData?.SalesManagerUserId : 0,
        KAMManagerUserId: retrieveData?.KAMManagerUserId ? retrieveData?.KAMManagerUserId : 0,
        RegionId: retrieveData?.RegionId ? retrieveData?.RegionId : 0,
      }))
    }
  }

  //#endregion

  //#region call API when get token
  useEffect(() => {
    async function fetchData(AgentId: number) {
      dispatch(loaderActions.start())
      let statusoftoken = await verifytoken();
      if (statusoftoken) {
        const user: any = GetLocalStorage();
        setFormValue((form) => ({ ...form, TravelAgentId: AgentId, UserId: user?.userMasterID ?? 0 }))
        setIsSaved((save: any) => ({ ...save, Assignment: true }));
        Promise.allSettled([
          gettravelagentassignmentbyid(AgentId)
        ])
        dispatch(loaderActions.end())
      }
      else {
        dispatch(loaderActions.end());
        ClearLocalStorage();
        dispatch({ type: 'SESSIONOUT', message: 'Your Session Expired. Please Login Again!' })
      }

    }
    const TAid = location?.state?.Id > 0 ? location?.state?.Id : TravelAgentId;
    TAid > 0 && fetchData(TAid);
  }, [location, TravelAgentId])
  //#endregion

  //#region call Save/Update function when click on "Yes" on tab change
  useEffect(() => {
    if (isConfirm === 1 && selectedTabId === tabId) {
      handleSaveUpdate();
    }
  }, [isConfirm])
  //#endregion

  //#region onChange functions
  const handleSelectChange = (name: string, e: any) => {
    setWarningMessages([]);
    setFormError(null);
    setIsSaved((save: any) => ({ ...save, Assignment: false }));
    setEditDisabled(false);
    setClearDisabled(false);
    if (name === "RegionId") {
      setFormValue((form) => ({ ...form, RegionId: e?.value ? e?.value : 0, SalesManagerUserId: 0, KAMManagerUserId: 0 }));
    }
    else {
      setFormValue((form) => ({ ...form, [name]: e?.value ? e?.value : 0 }))
    }
  }
  //#endregion

  //#region Warning Message function
  useImperativeHandle(ref, () => ({
    warningValidation() {
      if (formValue?.SalesManagerUserId === 0 && formValue?.KAMManagerUserId === 0) {
        return {
          tabId: tabId,
          errorMessages: { selectManagerValidation: "Please appoint at least Sales Manager or KAM." }
        }
      }
      else if (!isSaved?.Assignment) {
        return {
          tabId: tabId,
          errorMessages: { selectManagerValidation: "There are unsaved data, please save first." }
        }
      }
      else {
        return null;
      }
    }
  }))
  //#endregion

  const validateData = () => {
    setWarningMessages([]);
    let status = true;
    let error: any = {};

    if (formValue?.RegionId === 0) {
      error.RegionId = "Please select region.";
      status = false;
    }
    // if (formValue?.SalesManagerUserId === 0 && formValue?.KAMManagerUserId === 0) {
    //   error.SalesManagerUserId = "Please appoint at least Sales Manager or KAM.";
    //   status = false;
    // }

    setFormError(error);

    if (Object.keys(error)?.length > 0) {
      const id = Object.keys(error)?.[0];
      document.getElementById(id)?.focus();
    }
    return status;
  }

  //#region insert / update data function
  const handleSaveUpdate = async () => {
    setIsConfirm(isConfirm === 1 && selectedTabId === tabId ? 2 : 0);
    if (validateData()) {
      dispatch(loaderActions.start());
      const statusoftoken = await verifytoken();
      if (statusoftoken) {
        const insertupdatetravelagentassignment = await postUsersData('/midoffice/v1/insertupdatetravelagentassignment', formValue);
        if (insertupdatetravelagentassignment?.status) {
          setIsSaved((save: any) => ({ ...save, Assignment: true }));
          setIsRedirect(true);
          dispatch({ type: 'ALERTING', message: insertupdatetravelagentassignment?.message || ApiNotRespondMessage });
        }
        else {
          dispatch({ type: 'ALERTING', message: insertupdatetravelagentassignment?.message || ApiNotRespondMessage });
        }
        dispatch(loaderActions.end());
      }
      else {
        dispatch(loaderActions.end());
        ClearLocalStorage();
        dispatch({ type: 'SESSIONOUT', message: 'Your Session Expired. Please Login Again!' })
      }
    }
  }
  //#endregion

  //#region clear function
  const handleClear = () => {
    setWarningMessages([]);
    setFormError(null);
    setIsSaved((save: any) => ({ ...save, Assignment: false }));
    setFormValue((form) => ({
      ...form,
      SalesManagerUserId: 0,
      KAMManagerUserId: 0,
      RegionId: 0,
    }));
  }
  //#endregion

  //#region back function
  const handleBack = () => {
    navigate('/travel-agent-management');
  }
  //#endregion

  //#region get warning messages
  const selectManagerValidation = useMemo(() =>
    warningMessages?.find((fin) => fin?.tabId === tabId)?.errorMessages?.selectManagerValidation || null
    , [warningMessages])
  //#endregion

  const selectedRegion = useMemo(() => {
    return travelAgentOptionList?.regionList?.find((ele: regionAssignment) => ele.regionId === formValue?.RegionId)
  }, [travelAgentOptionList?.regionList, formValue?.RegionId])

  return (
    <div className='mt-5'>
      {
        selectManagerValidation ?
          selectManagerValidation && <div className="text-danger text-center mb-2 fs_14"> {selectManagerValidation} </div>
          :
          formError?.SalesManagerUserId && <div className="text-danger text-center mb-2 fs_14"> {formError?.SalesManagerUserId} </div>
      }

      <div className='row'>
        <ColumnsDevider label='Region/Zone' isRequired >
          <Select
            inputId='RegionId'
            className="dropdown select_dropdown custom_class"
            options={travelAgentOptionList?.regionList}
            value={travelAgentOptionList?.regionList?.filter((fil: any) => fil?.value === formValue?.RegionId)}
            onChange={(e: any) => handleSelectChange('RegionId', e)}
            placeholder="Region/Zone"
            isDisabled
            isSearchable
            isClearable
          />
          {formError?.RegionId && <div className="text-danger fs_14"> {formError?.RegionId} </div>}
        </ColumnsDevider>
      </div>

      <div className="row">
        <ColumnsDevider label='Assigned Sales manager'>
          <Select
            inputId='SalesManagerUserId'
            className="dropdown select_dropdown custom_class"
            options={selectedRegion?.salesManagerUsers}
            value={selectedRegion?.salesManagerUsers?.filter((fil) => fil?.value === formValue?.SalesManagerUserId) || null}
            onChange={(e: any) => handleSelectChange('SalesManagerUserId', e)}
            placeholder="Assigned Sales manager"
            isDisabled={disabled || domainWiseDisable?.assignmentField || domainWiseDisable?.isExternalUser}
            isSearchable
            isClearable
          />
        </ColumnsDevider>

        <ColumnsDevider label='Assigned KAM'>
          <Select
            inputId='KAMManagerUserId'
            className="dropdown select_dropdown custom_class"
            options={selectedRegion?.kamUsers}
            value={selectedRegion?.kamUsers?.filter((fil) => fil?.value === formValue?.KAMManagerUserId) || null}
            onChange={(e: any) => handleSelectChange('KAMManagerUserId', e)}
            placeholder="Assigned KAM"
            isDisabled={disabled || domainWiseDisable?.assignmentField || domainWiseDisable?.isExternalUser}
            isSearchable
            isClearable
          />
        </ColumnsDevider>
      </div>

      <div className="mt-3 d-flex justify-content-center mt-5">
        <button type="button" className="btn btn-primary mx-1 px-3" id="btnAdd" onClick={handleSaveUpdate} disabled={editDisabled || disabled || domainWiseDisable?.isExternalUser} > {domainWiseDisable?.loginUserDomainMasterId !== 1 ? "Save AS Draft" : (location?.state?.type === "Edit" ? "Update" : "Save")} </button>
        <button type="button" className="btn btn-primary mx-1 px-3" id="btnClear" onClick={handleClear} disabled={clearDisabled || disabled || domainWiseDisable?.isExternalUser} >Clear</button>
        <button type="button" className="btn btn-primary mx-1 px-3" id="btnBack" onClick={handleBack}  >Back</button>
      </div>
    </div>
  )
}
)


export default TravelAgentAssignment